import { Download, RefreshRounded, Update } from '@mui/icons-material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { Grid, Avatar, IconButton, Stack, Tooltip } from '@mui/material';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BsEye } from 'react-icons/bs';
import { MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  IVendorListsAggregationDTO,
  IMapValueSubMap,
  IVendorSearchFilterDTO,
  downloadVendorList,
  revalidate,
  hideListFromLists,
} from 'wisestep-api';
import {
  WsDataTable,
  WsDotColors,
  WsDotIcon,
  WsChip,
  WsIconPersons,
  WsChipVariants,
  WsIconJobOutline,
  BottomActionBar,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import { ActivateFeature } from '../../../../components/ActivateFeature';
import VendorEmail from '../../../../components/VendorEmail';
import { vendorEmailObject } from '../../../../components/VendorEmail/VendorEmail';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { downloadHelper } from '../../../dataDownload/helper/PayloadHelper';
import useConsultantsStore from '../../../importConsultants/listing/hooks/useConsultantsStore';
import useJobsStore from '../../../jobs/listing/hooks/useJobsStore';
import { vendorsSearchDefaultQuery } from '../../utils/vendorsHelper';

export type IconProps = {
  statusColor: string;
};
interface VendorListTableProps {
  data?: IVendorListsAggregationDTO;
  pageRefresh: () => void;
  creditsBalance: number;
}

const VendorsListTable = ({ data, pageRefresh, creditsBalance }: VendorListTableProps) => {
  const navigate = useNavigate();
  const [broadCast, setBroadCast] = useState<vendorEmailObject>({} as vendorEmailObject);
  const [emailsLength, setEmailsLength] = useState<number>(0);

  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const [openConsultant, setOpenConsultant] = useState<boolean>(false);
  const selectedJobs = useJobsStore((state) => state.jobs);
  const updateSelectedJobs = useJobsStore((state) => state.updateSelectedJobs);

  const setConsultants = useConsultantsStore((state) => state.setSelectedConsultants);
  const selectedConsultants = useConsultantsStore((state) => state.consultants);
  const workspace = useWorkspaceContext();
  const isAdmin = workspace.owns;
  const getValueKeys: IMapValueSubMap[] = Object.values(data?.aggregationResult['vendorsInLists']);
  const DATA = getValueKeys.map((val: IMapValueSubMap) => ({
    name: val.keyObject.name,
    id: val.keyObject.id,
    email: val.keyObject.createdBy,
    contentType: val.keyObject.contentType,
    access: val.keyObject,
    valid: val.subKeyValues['Valid'],
    invalid: val.subKeyValues['Invalid'],
    pending: val,
    unknown: val.subKeyValues['Unknown'],
    details: val.keyObject.id,
    actions: {
      vendor: val.keyObject,
      valid: val.subKeyValues['Valid'],
    },
  }));
  const setNotification = useNotificationStore((state) => state.setNotification);

  const DownloadVendorList = async (id: string) => {
    const query: IVendorSearchFilterDTO = vendorsSearchDefaultQuery;
    query.vendorsInLists = [id];
    try {
      const response = await downloadVendorList(query);
      downloadHelper(response);
      setNotification(success('Downloaded candidates'));
    } catch {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };

  const Revalidate = async (id: string) => {
    const query: IVendorSearchFilterDTO = vendorsSearchDefaultQuery;
    query.vendorsInLists = [id];
    try {
      await revalidate(query);
      setNotification(
        success(`Processing Revalidation Request
      It might take some time to finish, and we don't want to keep you waiting. We will send you an email after validation is complete.`)
      );
      pageRefresh();
    } catch (err) {
      if (`${err}`.includes('409')) {
        setNotification(warning('Revalidation request is already in process'));
      } else {
        setNotification(warning('Something went wrong, Please try again.'));
      }
    }
  };
  const deleteVendorList = async (id: string) => {
    try {
      await hideListFromLists(id);
      setNotification(success(`List got deleted successfully`));
      pageRefresh();
    } catch (err) {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };
  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Access',
      accessor: 'access',
      Cell: (cell: Cell) => (
        <Stack direction="row" alignItems="center">
          <WsChip value={cell.value.type} variant={WsChipVariants.PRIMARY} />
          <IconButton
            onClick={() =>
              navigate(
                `/vendors/new-vendor-list?id=${cell.value.id}&type=${cell.value.type}&contentType=${cell.value.contentType}`
              )
            }
          >
            <Tooltip title="Update">
              <Update />
            </Tooltip>
          </IconButton>
        </Stack>
      ),
    },
    {
      Header: 'Content Type',
      accessor: 'contentType',
      Cell: (cell: Cell) => (
        <WsChip
          icon={cell.value === 'Jobs' ? <WsIconJobOutline /> : <WsIconPersons />}
          key={cell.value}
          value={cell.value}
          variant={cell.value === 'Jobs' ? WsChipVariants.SECONDARY : WsChipVariants.TERTIARY}
        />
      ),
    },
    {
      Header: 'Valid',
      accessor: 'valid',
      Cell: (cell: Cell) => <WsDotIcon value={cell.value} color={WsDotColors.GREEN} />,
    },
    {
      Header: 'Invalid',
      accessor: 'invalid',
      Cell: (cell: Cell) => <WsDotIcon value={cell.value} color={WsDotColors.RED} />,
    },
    {
      Header: 'Pending',
      accessor: 'pending',
      Cell: (cell: Cell) => (
        <Stack direction="row">
          <WsDotIcon
            value={cell.value.subKeyValues['ValidationPending']}
            color={WsDotColors.YELLOW}
          />
          <IconButton onClick={() => Revalidate(cell.value.keyObject.id)}>
            <Tooltip title="Revalidate">
              <RefreshRounded />
            </Tooltip>
          </IconButton>
        </Stack>
      ),
    },
    {
      Header: 'Unknown',
      accessor: 'unknown',
      Cell: (cell: Cell) => <WsDotIcon value={cell.value} color={WsDotColors.SENARY} />,
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: (cell) => (
        <ActivateFeature feature="VS_BROADCAST_JOBS_CANDIDATES">
          <WsButton
            variant="outlined"
            startIcon={<CampaignOutlinedIcon />}
            onClick={() => {
              setEmailsLength(cell.value.valid);
              setBroadCast({
                id: [cell.value.vendor.id],
                name: [cell.value.vendor.name],
              } as vendorEmailObject);
              if (emailsLength > creditsBalance) {
                setNotification(failed('Broadcast quota is exhausted for the day'));
              } else {
                if (cell.value.vendor.contentType === 'Jobs') {
                  setOpenEmail(true);
                  setOpenConsultant(false);
                } else {
                  setOpenEmail(false);
                  setOpenConsultant(true);
                }
              }
            }}
          >
            Broadcast
          </WsButton>
        </ActivateFeature>
      ),
    },
    {
      Header: 'Deatils',
      accessor: 'details',
      Cell: (cell: Cell) => (
        <Stack direction="row" rowGap={1}>
          <IconButton onClick={() => navigate(`/vendors/${cell.value}`)}>
            <BsEye />
          </IconButton>
          <ActivateFeature feature="VS_DOWNLOAD_VENDORS">
            <IconButton onClick={() => DownloadVendorList(cell.value)}>
              <Download />
            </IconButton>
          </ActivateFeature>
          {isAdmin && (
            <IconButton onClick={() => deleteVendorList(cell.value)}>
              <MdDelete color={WsColors.Red500} />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const getSelectedVendors = () => {
    const id = selectedRows.map((rowIndex) => DATA[rowIndex].id ?? '');
    const names = selectedRows.map((rowIndex) => DATA[rowIndex].name ?? '');
    return { id: id, name: names };
  };
  const getSelectedValidEmailsCount = () =>
    selectedRows
      .map((rowIndex) => Number(DATA[rowIndex].valid) ?? 0)
      .reduce<number>((accumulator, current) => {
        return accumulator + current;
      }, 0);

  return (
    <>
      <WsDataTable
        columns={columns}
        data={DATA || []}
        totalDataItems={DATA.length}
        selectable={true}
        // rowActions={[
        //   {
        //     label: 'Remove',
        //     // eslint-disable-next-line prettier/prettier
        //     callback: () => { },
        //   },
        //   {
        //     label: 'Add',
        //     // eslint-disable-next-line prettier/prettier
        //     callback: () => { },
        //   },
        // ]}
        getSelectedRows={(idArray) => {
          setSelectedRows(idArray);
        }}
        boldItems={['name']}
        pageable={false}
      />

      {selectedRows.length > 0 && (
        <BottomActionBar>
          <Grid item container justifyContent={'space-between'} px={6}>
            <Grid item container xs={7} display="flex">
              <Grid item pr={2} display="flex">
                <Avatar
                  variant="square"
                  sx={{ bgcolor: WsColors.TheBlues400, width: 24, height: 24 }}
                >
                  <WsTypography color={WsTypoColors.WHITE}>{selectedRows.length}</WsTypography>
                </Avatar>
                <WsTypography color={WsTypoColors.PRIMARY}>Vendor(s) Selected</WsTypography>
              </Grid>
            </Grid>

            <WsButton
              onClick={() => {
                setEmailsLength(getSelectedValidEmailsCount());
                if (emailsLength > creditsBalance) {
                  setNotification(failed('Broadcast quota is exhausted for the day'));
                } else {
                  setOpenConsultant(true);
                  setOpenEmail(false);
                }
              }}
            >
              Broadcast Talent Bench
            </WsButton>
            <WsButton
              onClick={() => {
                setEmailsLength(getSelectedValidEmailsCount());
                if (emailsLength > creditsBalance) {
                  setNotification(failed('Broadcast quota is exhausted for the day'));
                } else {
                  setOpenEmail(true);
                  // onBroadcastJobs;
                  setOpenConsultant(false);
                }
              }}
            >
              Broadcast Jobs
            </WsButton>
          </Grid>
        </BottomActionBar>
      )}
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {openEmail && (
          <VendorEmail
            open={openEmail}
            handleClose={() => {
              setOpenEmail(false);
              selectedRows[0];
              updateSelectedJobs(null);
            }}
            pageRefersh={() => pageRefresh()}
            vendorsInLists={selectedRows.length > 0 ? getSelectedVendors() : broadCast}
            jobsOnly={selectedJobs && selectedJobs.length > 0 ? false : true}
            consultantsOnly={false}
            selectedJobs={selectedJobs}
            size={emailsLength}
          />
        )}
        {openConsultant && (
          <VendorEmail
            open={openConsultant}
            handleClose={() => {
              setOpenConsultant(false);
              selectedRows[0];
              setConsultants([]);
            }}
            pageRefersh={() => pageRefresh()}
            vendorsInLists={selectedRows.length > 0 ? getSelectedVendors() : broadCast}
            jobsOnly={false}
            consultantsOnly={selectedConsultants && selectedConsultants.length > 0 ? false : true}
            selectedConsultants={selectedConsultants}
            size={emailsLength}
          />
        )}
      </ErrorBoundary>
    </>
  );
};

export default VendorsListTable;
